import { createContext, useContext } from "react";

/**
 * Token validation states
 * 
 * @see docs/TOKEN_VALIDATION.md for detailed documentation
 * 
 * - 'pending': Initial state, validation in progress
 * - 'valid': Token verified successfully with backend
 * - 'invalid': Token expired/invalid (auth data will be cleared)
 * - 'offline': Cannot validate (offline mode, token is trusted)
 */
export type TokenValidationState = 'pending' | 'valid' | 'invalid' | 'offline';

/**
 * Authentication state interface
 * 
 * @see docs/TOKEN_VALIDATION.md for token validation system documentation
 */
export interface AuthState {
  token: string | null;
  email: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokenValidation: TokenValidationState;
}

export interface AuthContextValue extends AuthState {
  login: (token: string, email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuthState: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useIsAuthenticated = (): boolean => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
};